import {
  useEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import cx from "classnames";
import { UseComboboxReturnValue as UseDownshiftComboboxLegacyReturnValue } from "downshift";

import { useComboboxLegacy, ADD_NEW } from "./ComboboxLegacy";

import { Icon } from "../Icon";
import { Flex, FlexProps } from "../Flex";

import styles from "./ComboboxLegacy.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";
import listViewStyles from "../ListView/ListView.module.scss";
import checkboxStyles from "../Checkbox/Checkbox.module.scss";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import CheckboxOutline from "@servicetitan/hammer-icon/mdi/round/check_box_outline_blank.svg";
import CheckboxChecked from "@servicetitan/hammer-icon/mdi/round/check_box.svg";
import CheckboxIndeterminate from "@servicetitan/hammer-icon/mdi/round/indeterminate_check_box.svg";

type ComboboxLegacyItemSharedProps = ComponentPropsWithoutRef<"li" | "div"> & {
  children?: ReactNode;
  disabled?: boolean;

  alignItems?: FlexProps["alignItems"];
  justifyItems?: FlexProps["justifyItems"];
  alignContent?: FlexProps["alignContent"];
  justifyContent?: FlexProps["justifyContent"];
  placeItems?: FlexProps["placeItems"];
  placeContent?: FlexProps["placeContent"];
  gap?: FlexProps["gap"];
  rowGap?: FlexProps["rowGap"];
  columnGap?: FlexProps["columnGap"];
};

export type ComboboxLegacyItemProps<Item> = ComboboxLegacyItemSharedProps & {
  item: Item;
  index: number;
};

export type ComboboxLegacyItemAddNewProps<Item> =
  ComboboxLegacyItemSharedProps & {
    onSelection?: (
      inputValue: UseDownshiftComboboxLegacyReturnValue<Item>["inputValue"],
    ) => void;
  };

export const ComboboxLegacyItem = function <Item = any>({
  children,
  disabled: disabledProp,
  item,
  index,
  alignItems,
  justifyItems,
  alignContent,
  justifyContent,
  placeItems,
  placeContent,
  gap,
  rowGap,
  columnGap,
  ...rest
}: ComboboxLegacyItemProps<Item>) {
  const {
    itemToKey,
    getItemProps,
    multiple,
    selectedItem,
    selectedItems,
    disabledItems,
    setDisabledItems,
    highlightedIndex,
  } = useComboboxLegacy<Item>();

  const disabled =
    disabledProp ||
    (item && item instanceof Object && "disabled" in item && item.disabled);

  useEffect(() => {
    if (disabledProp && !disabledItems.includes(item)) {
      setDisabledItems([...disabledItems, item]);
    } else if (disabledItems.includes(item)) {
      setDisabledItems(disabledItems.filter((prevItem) => prevItem === item));
    }

    return () => {
      if (disabledItems.includes(item)) {
        setDisabledItems(disabledItems.filter((prevItem) => prevItem === item));
      }
    };
  }, [item, disabledProp, setDisabledItems]); //eslint-disable-line react-hooks/exhaustive-deps

  if (multiple) {
    const checked = selectedItems.some(
      (selectedItem) => itemToKey(selectedItem) === itemToKey(item),
    );

    return (
      <div
        data-anv="combobox-item"
        {...getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"div">),
        })}
        className={cx(
          styles["listview-option"],
          listViewStyles["listview-option"],
        )}
      >
        <div className={cx(styles["cell"], listViewStyles["cell"])}>
          <div className={checkboxStyles["wrapper"]}>
            <div className={checkboxStyles["checkbox"]}>
              <input
                type="checkbox"
                checked={checked}
                aria-checked={checked}
                aria-label="Select"
                readOnly
                {...(disabled ? { disabled: true } : {})}
              />

              <span className={checkboxStyles["icon-wrapper"]} aria-hidden>
                <Icon
                  size="large"
                  svg={CheckboxIndeterminate}
                  className={checkboxStyles["icon-indeterminate"]}
                />

                <Icon
                  size="large"
                  svg={CheckboxChecked}
                  className={checkboxStyles["icon-checked"]}
                />

                <Icon
                  size="large"
                  svg={CheckboxOutline}
                  className={checkboxStyles["icon-unchecked"]}
                />
              </span>

              <span aria-hidden></span>
            </div>
          </div>
        </div>

        <Flex
          grow={1}
          shrink={1}
          alignItems={alignItems}
          justifyItems={justifyItems}
          alignContent={alignContent}
          justifyContent={justifyContent}
          placeItems={placeItems}
          placeContent={placeContent}
          gap={gap}
          rowGap={rowGap}
          columnGap={columnGap}
        >
          {children}
        </Flex>
      </div>
    );
  } else {
    return (
      <li
        {...getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"li">),
        })}
        data-anv="combobox-item"
        data-checked={itemToKey(selectedItem) === itemToKey(item)}
        className={styles["item"]}
        {...(disabled ? { "aria-disabled": true } : {})}
      >
        <Flex
          className={listboxStyles["children"]}
          grow={1}
          shrink={1}
          alignItems={alignItems}
          justifyItems={justifyItems}
          alignContent={alignContent}
          justifyContent={justifyContent}
          placeItems={placeItems}
          placeContent={placeContent}
          gap={gap}
          rowGap={rowGap}
          columnGap={columnGap}
        >
          {children}
        </Flex>
        <Icon
          aria-hidden
          svg={Check}
          inherit
          className={styles["Icon"]}
          style={{
            opacity: itemToKey(selectedItem) === itemToKey(item) ? 1 : 0,
          }}
        />
      </li>
    );
  }
};

export const ComboboxLegacyItemAddNew = function <Item = any>({
  onSelection,
  ...props
}: ComboboxLegacyItemAddNewProps<Item>) {
  const { items, setHasAddNew, hasExactMatch, setOnSelectItemAddNew } =
    useComboboxLegacy<Item>();

  useEffect(() => {
    setHasAddNew?.(true);

    return () => {
      setHasAddNew?.(false);
    };
  }, []);

  useEffect(() => {
    setOnSelectItemAddNew?.(() => onSelection);

    return () => {
      setOnSelectItemAddNew?.(undefined);
    };
  }, [setOnSelectItemAddNew, onSelection]);

  // hide the "Add New" item on our initial render so it doesn't flicker
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  return !hasExactMatch && !firstRenderRef.current ? (
    <ComboboxLegacyItem<Item>
      {...props}
      item={ADD_NEW as Item}
      index={items.length}
    />
  ) : null;
};
